import React from 'react';
import PropTypes from 'prop-types';
import {FormattedDate, FormattedMessage, FormattedTime} from "react-intl";

import {Hidden} from "@mui/material";
import {styled} from "@mui/material/styles";

import Badge from "components/Badge";
import TruncationTooltip from "components/CustomTooltip/TruncationTooltip";
import ListingProductDetails from "./ListingProductDetails";
import pickLastUpdateVariant from "./lastUpdateVariant";
import {
    black500,
    blue50,
    blue100,
    blue700,
    greyishBlue50,
    greyishBlue100,
    greyishBlue400,
    greyishBlue600,
    red50,
    red100,
    red700
} from "assets/jss/main";

ListingArticleCell.propTypes = {
    name: PropTypes.string,
    isEikow: PropTypes.bool,
    brandName: PropTypes.string,
    sku: PropTypes.node,
    eshopId: PropTypes.node,
    categories: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        draft: PropTypes.bool,
    })),
    updatedAt: PropTypes.string,
    // Last edit made by a human, and its author. Takes precedence over updatedAt
    // when set: it is the only one that says a person changed something, where
    // updatedAt also moves on every feed import.
    manualUpdatedAt: PropTypes.string,
    manualUpdatedBy: PropTypes.string,
};

// 224px, fixed. Every column but the last one
// has a fixed width in the DS; a width rather than a max-width is what stops the cell
// from claiming the max-content width of a nowrap product name instead of ellipsising
// it, since the cells shrink to content (see ListingTableCell).
const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '224px'
});

const Name = styled('div')({
    color: black500,
    fontSize: '14px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const SubtitleContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
    minWidth: 0
});

const BrandName = styled('span')({
    color: greyishBlue600,
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const CategoriesContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px'
});

// Badge's label does not truncate on its own
const TruncatedLabel = styled('span')({
    display: 'block',
    maxWidth: '160px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
});

const Divider = styled('div')({
    width: '100%',
    borderTop: `1px dashed ${greyishBlue50}`
});

const UpdatedAt = styled('div')({
    color: greyishBlue400,
    fontSize: '12px'
});

// every product either comes from Eikow or from the feed; shared with the
// mobile article cell
export function OriginBadge({isEikow}) {
    if (isEikow) {
        return (
            <Badge rounded backgroundColor={blue50} labelColor={blue700} border borderColor={blue100}>
                <FormattedMessage id={"retailer.product.list.article.origin.eikow"}/>
            </Badge>
        );
    }

    return (
        <Badge rounded color="info" border borderColor={greyishBlue100}>
            <FormattedMessage id={"retailer.product.list.article.origin.feed"}/>
        </Badge>
    );
}

const shortDate = (value) => <FormattedDate value={value} day="2-digit" month="2-digit" year="numeric"/>;

// See pickLastUpdateVariant for what each variant means and why there are three.
const LAST_UPDATE_MESSAGES = {
    global: "retailer.product.list.article.updatedAt",
    manual: "retailer.product.list.article.manualUpdatedAt",
    both: "retailer.product.list.article.updatedAtWithManual",
};

function LastUpdate({updatedAt, manualUpdatedAt, manualUpdatedBy}) {
    const variant = pickLastUpdateVariant({updatedAt, manualUpdatedAt, manualUpdatedBy});

    if ('none' === variant) {
        return null;
    }

    // 'manual' shows the human edit alone, so it is the date that line carries.
    const primary = 'manual' === variant ? manualUpdatedAt : updatedAt;
    const values = {date: shortDate(primary), time: <FormattedTime value={primary}/>};

    if ('manual' === variant) {
        values.author = manualUpdatedBy;
    }

    if ('both' === variant) {
        values.author = manualUpdatedBy;
        values.manualDate = shortDate(manualUpdatedAt);
        values.manualTime = <FormattedTime value={manualUpdatedAt}/>;
    }

    return (
        <Hidden smDown>
            <Divider/>
            <UpdatedAt>
                <FormattedMessage id={LAST_UPDATE_MESSAGES[variant]} values={values}/>
            </UpdatedAt>
        </Hidden>
    );
}

function ListingArticleCell({name, isEikow = false, brandName, sku, eshopId, categories = [], updatedAt, manualUpdatedAt, manualUpdatedBy}) {
    return (
        <Container>
            <div>
                <TruncationTooltip title={name ?? ''} placement="top">
                    <Name>{name}</Name>
                </TruncationTooltip>
                <Hidden smDown>
                    <SubtitleContainer>
                        <OriginBadge isEikow={isEikow}/>
                        {brandName && <BrandName>{brandName}</BrandName>}
                    </SubtitleContainer>
                </Hidden>
            </div>
            <Hidden smDown>
                <ListingProductDetails
                    details={[
                        {label: <FormattedMessage id={"retailer.product.list.label.sku"}/>, value: sku},
                        {label: <FormattedMessage id={"retailer.product.list.label.eshopid"}/>, value: eshopId},
                    ]}
                />
            </Hidden>
            {categories.length > 0 && (
                <Hidden smDown>
                    <CategoriesContainer>
                        {categories.map((category) => (
                            <Badge
                                key={category.id}
                                backgroundColor={category.draft ? red50 : greyishBlue50}
                                labelColor={category.draft ? red700 : black500}
                                border
                                borderColor={category.draft ? red100 : greyishBlue100}
                                indicator={category.draft ? 'circle' : false}
                                indicatorColor={red700}
                            >
                                <TruncationTooltip title={category.name ?? ''} placement="top">
                                    <TruncatedLabel>{category.name}</TruncatedLabel>
                                </TruncationTooltip>
                            </Badge>
                        ))}
                    </CategoriesContainer>
                </Hidden>
            )}
            <LastUpdate
                updatedAt={updatedAt}
                manualUpdatedAt={manualUpdatedAt}
                manualUpdatedBy={manualUpdatedBy}
            />
        </Container>
    );
}

export default ListingArticleCell;
